<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import NonSalesAPI from 'dashboard/api/nonSales';
import FollowUpsAPI from 'dashboard/api/followUps';

const emit = defineEmits(['non-sale-created']);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('auth/getCurrentUser');

const isOpen = ref(false);
const isSubmitting = ref(false);
const conversationId = ref(null);
const conversationDbId = ref(null);
const pendingFollowUps = ref([]);
const showMarkDone = ref(false);

const form = reactive({
  reason: '',
  returnInDays: '',
  followUpAgentId: '',
});

const agentOptions = computed(() =>
  (agents.value || []).map(a => ({ value: a.id, label: a.name }))
);

const hasReturnDays = computed(() => form.returnInDays && Number(form.returnInDays) > 0);

onMounted(() => {
  store.dispatch('agents/get');
});

function open(convId) {
  conversationId.value = convId;
  form.reason = '';
  form.returnInDays = '';
  form.followUpAgentId = currentUser.value?.id ?? '';
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

async function handleSubmit() {
  if (!form.reason.trim()) return;

  isSubmitting.value = true;
  try {
    const { data: nonSale } = await NonSalesAPI.createForConversation(
      conversationId.value,
      { reason: form.reason.trim() }
    );

    if (hasReturnDays.value) {
      const days = Number(form.returnInDays);
      const scheduled = new Date();
      scheduled.setDate(scheduled.getDate() + days);
      const scheduledFor = scheduled.toISOString().slice(0, 10);

      await FollowUpsAPI.create({
        conversation_id: nonSale.conversation_id,
        scheduled_for: scheduledFor,
        source_type: 'NonSale',
        source_id: nonSale.id,
        agent_id: form.followUpAgentId || currentUser.value?.id,
      });
    } else {
      const { data: followUps } = await FollowUpsAPI.get({
        conversation_id: nonSale.conversation_id,
      }).catch(() => ({ data: [] }));
      const pending = (Array.isArray(followUps) ? followUps : []).filter(
        f => f.status === 'pending'
      );
      if (pending.length > 0) {
        conversationDbId.value = nonSale.conversation_id;
        pendingFollowUps.value = pending;
        showMarkDone.value = true;
        isSubmitting.value = false;
        emit('non-sale-created', nonSale);
        return;
      }
    }

    useAlert(t('SALES.NON_SALE.SUCCESS'));
    emit('non-sale-created', nonSale);
    close();
  } catch {
    useAlert(t('SALES.NON_SALE.ERROR'), { variant: 'error' });
  } finally {
    isSubmitting.value = false;
  }
}

async function handleMarkDoneAndClose(shouldMark) {
  if (shouldMark && pendingFollowUps.value.length > 0) {
    await Promise.allSettled(pendingFollowUps.value.map(f => FollowUpsAPI.markDone(f.id)));
  }
  showMarkDone.value = false;
  pendingFollowUps.value = [];
  useAlert(t('SALES.NON_SALE.SUCCESS'));
  close();
}

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

      <!-- Tela de marcar retorno como feito -->
      <div
        v-if="showMarkDone"
        class="relative z-10 w-full max-w-md flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl"
      >
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('SALES.FOLLOW_UP.MARK_DONE_PROMPT') }}
          </h2>
        </div>
        <div class="px-5 py-4 flex items-start gap-3 rounded-xl">
          <span class="i-lucide-calendar-check text-lg text-n-brand shrink-0 mt-0.5" />
          <p class="text-sm text-n-slate-10">
            {{ pendingFollowUps.length === 1
              ? 'Há 1 retorno pendente nesta conversa.'
              : `Há ${pendingFollowUps.length} retornos pendentes nesta conversa.` }}
          </p>
        </div>
        <div class="flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="handleMarkDoneAndClose(false)"
          >
            {{ $t('SALES.NON_SALE.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white hover:brightness-110 transition-all"
            @click="handleMarkDoneAndClose(true)"
          >
            {{ $t('SALES.FOLLOW_UP.DONE') }}
          </button>
        </div>
      </div>

      <div v-else class="relative z-10 w-full max-w-md flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('SALES.NON_SALE.TITLE') }}
          </h2>
          <button
            class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x text-lg" />
          </button>
        </div>

        <!-- Body -->
        <div class="px-5 py-4 flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('SALES.NON_SALE.REASON_LABEL') }}
              <span class="text-s-red-500 ml-0.5">*</span>
            </label>
            <textarea
              v-model="form.reason"
              rows="3"
              :placeholder="$t('SALES.NON_SALE.REASON_PLACEHOLDER')"
              class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
            />
          </div>

          <!-- Agendamento de retorno -->
          <div class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3">
            <p class="text-xs font-medium text-n-slate-11">
              {{ $t('SALES.NON_SALE.SCHEDULE_LABEL') }}
            </p>

            <div class="flex items-center gap-2">
              <input
                v-model="form.returnInDays"
                type="number"
                min="1"
                max="365"
                placeholder="—"
                class="w-24 rounded-lg border border-n-strong bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
              <span class="text-sm text-n-slate-11">{{ $t('SALES.NON_SALE.DAYS') }}</span>
            </div>

            <!-- Agente do retorno — só aparece quando dias preenchidos -->
            <div v-if="hasReturnDays" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                {{ $t('SALES.FOLLOW_UP.AGENT_LABEL') }}
              </label>
              <select
                v-model="form.followUpAgentId"
                class="w-full rounded-lg border border-n-strong bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              >
                <option
                  v-for="agent in agentOptions"
                  :key="agent.value"
                  :value="agent.value"
                >
                  {{ agent.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            {{ $t('SALES.NON_SALE.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white hover:bg-n-brand-dark transition-colors disabled:opacity-50 flex items-center gap-1.5"
            :disabled="isSubmitting || !form.reason.trim()"
            @click="handleSubmit"
          >
            <span v-if="isSubmitting" class="i-lucide-loader-circle animate-spin text-sm" />
            {{ $t('SALES.NON_SALE.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
